import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, Check, ChevronsDownUp, ChevronsUpDown } from 'lucide-react';

import { s, brand, gray, ink, amber, white, radius, shadow } from './theme';
import { SECTION_ORDER } from './fields';
import Toggle from './ui/Toggle';
import { useScrollContainer } from './scrollContainer';
import { useProgramForm } from '../../../context/ProgramFormContext';

/**
 * Sidebar: completion meter, expand/collapse, required-only filter, and the section list
 * with scroll spy.
 *
 * Ported from `Program Entry Updates/src/components/SectionNav.tsx` minus the Publish
 * toggle and status badge — this API has no draft state, so there is nothing to publish.
 */

// Breathing room above a section once it has been scrolled into view.
const HEADER_OFFSET = 16;

const SectionNav = () => {
    const {
        progressBySection,
        issuesBySection,
        overall,
        openSection,
        setAllSections,
        allExpanded,
        requiredOnly,
        setRequiredOnly,
    } = useProgramForm();

    const [active, setActive] = useState(SECTION_ORDER[0].id);
    const lockRef = useRef(null);
    const navRef = useRef(null);

    // Inside the modal the form scrolls in `.modal-body`, not the window. Everything
    // below measures against whichever it is.
    const containerRef = useScrollContainer();

    // The nav only pins on desktop, where it sits in its own column. Stacked above the
    // form on narrow screens it would eat the viewport. Bootstrap has no responsive
    // `sticky-*` utility, and inline styles carry no media queries, so it is measured.
    const [pinned, setPinned] = useState(
        () => typeof window !== 'undefined' && window.matchMedia('(min-width: 992px)').matches
    );

    useEffect(() => {
        const query = window.matchMedia('(min-width: 992px)');
        const onChange = event => setPinned(event.matches);
        query.addEventListener('change', onChange);
        return () => query.removeEventListener('change', onChange);
    }, []);

    const computeActive = useCallback(() => {
        const container = containerRef.current;

        const atBottom = container
            ? container.scrollTop + container.clientHeight >= container.scrollHeight - 4
            : window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 4;

        if (atBottom) {
            const visible = SECTION_ORDER.filter(({ id }) => document.getElementById(id));
            if (visible.length) setActive(visible[visible.length - 1].id);
            return;
        }

        // Everything is measured from the top of the scrolling box.
        const originTop = container ? container.getBoundingClientRect().top : 0;

        let current = SECTION_ORDER[0].id;
        SECTION_ORDER.forEach(({ id }) => {
            const el = document.getElementById(id);
            if (!el) return;
            if (el.getBoundingClientRect().top - originTop - HEADER_OFFSET - 24 <= 0) current = id;
        });
        setActive(current);
    }, [containerRef]);

    useEffect(() => {
        const container = containerRef.current;
        const target = container || window;

        const onScroll = () => {
            // While a click-scroll is in flight the highlight stays where the user put it.
            if (lockRef.current !== null) return;
            computeActive();
        };

        target.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll);
        computeActive();

        return () => {
            target.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', onScroll);
        };
    }, [computeActive, containerRef]);

    // Scrolling to the end of the form left the matching nav entries below the nav's own
    // fold, so the highlight moved somewhere the user could not see. This nudges the active
    // entry back into view, moving the nav's own scrollTop by hand. Deliberately not
    // `scrollIntoView`: that walks up every scrollable ancestor and would drag the modal
    // body along with it, fighting the scroll the user just made.
    useEffect(() => {
        if (!pinned) return;

        const container = navRef.current;
        const el = container && container.querySelector(`[data-section="${active}"]`);
        if (!el) return;

        const top = el.offsetTop;
        const bottom = top + el.offsetHeight;

        if (top < container.scrollTop) {
            container.scrollTop = top;
        } else if (bottom > container.scrollTop + container.clientHeight) {
            container.scrollTop = bottom - container.clientHeight;
        }
    }, [active, pinned]);

    const goToSection = id => {
        setActive(id);
        openSection(id);

        if (lockRef.current !== null) window.clearTimeout(lockRef.current);
        lockRef.current = window.setTimeout(() => {
            lockRef.current = null;
            computeActive();
        }, 800);

        // Two frames: one for the section to expand, one for the layout to settle.
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                const el = document.getElementById(id);
                if (!el) return;

                const container = containerRef.current;
                if (container) {
                    const delta =
                        el.getBoundingClientRect().top - container.getBoundingClientRect().top;
                    container.scrollTo({
                        top: container.scrollTop + delta - HEADER_OFFSET,
                        behavior: 'smooth',
                    });
                } else {
                    window.scrollTo({
                        top: el.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET,
                        behavior: 'smooth',
                    });
                }
            });
        });
    };

    const visibleSections = SECTION_ORDER.filter(
        ({ id }) => !requiredOnly || progressBySection[id].total > 0
    );

    return (
        <nav
            aria-label="Form sections"
            ref={navRef}
            className="program-form-nav"
            // Pinned, the nav is as tall as its content and the modal body scrolls past it,
            // so anything below the fold was unreachable. Capping it against the viewport
            // and giving it its own scrollbar keeps all seven sections in reach. The
            // subtraction covers the modal's inset, its header and the body padding —
            // measured against `.program_form_modal`'s `calc(100vh - 40px)`.
            style={
                pinned
                    ? {
                        position: 'sticky',
                        top: '8px',
                        maxHeight: 'calc(100vh - 190px)',
                        overflowY: 'auto',
                        overflowX: 'hidden',
                    }
                    : {}
            }
        >
            <div
                style={{
                    borderRadius: radius.xl,
                    border: `1px solid ${gray[200]}`,
                    backgroundColor: white,
                    padding: '12px',
                    boxShadow: shadow.card,
                }}
            >
                {/* Completion meter */}
                <div style={{ padding: '4px 8px 12px' }}>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '8px',
                        }}
                    >
                        <span
                            style={{
                                fontSize: '11px',
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                color: ink[400],
                            }}
                        >
                            Completion
                        </span>
                        <span style={{ fontSize: '13px', fontWeight: 700, color: ink[900] }}>
                            {overall.percent}%
                        </span>
                    </div>

                    <div
                        style={{
                            marginTop: '8px',
                            height: '6px',
                            width: '100%',
                            overflow: 'hidden',
                            borderRadius: radius.pill,
                            backgroundColor: gray[100],
                        }}
                        role="progressbar"
                        aria-valuenow={overall.percent}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-label="Required fields completed"
                    >
                        <div
                            style={{
                                height: '100%',
                                width: `${overall.percent}%`,
                                borderRadius: radius.pill,
                                backgroundColor: brand[600],
                                transition: 'width 0.3s',
                            }}
                        />
                    </div>

                    <p style={{ ...s.hint, marginTop: '6px' }}>
                        {overall.filled} of {overall.total} required fields
                    </p>
                </div>

                {/* Controls */}
                <div style={{ borderTop: `1px solid ${gray[100]}`, padding: '8px 4px' }}>
                    <button
                        type="button"
                        onClick={() => setAllSections(!allExpanded)}
                        aria-pressed={allExpanded}
                        style={{
                            display: 'flex',
                            width: '100%',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px',
                            borderRadius: radius.md,
                            border: `1px solid ${gray[300]}`,
                            backgroundColor: white,
                            padding: '8px 10px',
                            fontSize: '12px',
                            fontWeight: 600,
                            color: ink[700],
                            cursor: 'pointer',
                            transition: 'background-color 0.15s',
                        }}
                        onMouseEnter={e => {
                            e.currentTarget.style.backgroundColor = brand[50];
                            e.currentTarget.style.borderColor = brand[300];
                            e.currentTarget.style.color = brand[700];
                        }}
                        onMouseLeave={e => {
                            e.currentTarget.style.backgroundColor = white;
                            e.currentTarget.style.borderColor = gray[300];
                            e.currentTarget.style.color = ink[700];
                        }}
                    >
                        {allExpanded ? (
                            <ChevronsDownUp size={14} aria-hidden="true" />
                        ) : (
                            <ChevronsUpDown size={14} aria-hidden="true" />
                        )}
                        {allExpanded ? 'Collapse all' : 'Expand all'}
                    </button>

                    <div
                        style={{
                            marginTop: '8px',
                            borderRadius: radius.md,
                            border: `1px solid ${gray[200]}`,
                            padding: '8px 10px',
                        }}
                    >
                        <Toggle
                            id="required-only"
                            label="Show required only"
                            checked={requiredOnly}
                            onChange={setRequiredOnly}
                            labelStyle={{ color: ink[700] }}
                        />
                    </div>
                </div>

                {/* Section list */}
                <ul
                    style={{
                        borderTop: `1px solid ${gray[100]}`,
                        paddingTop: '8px',
                        margin: 0,
                        listStyle: 'none',
                        paddingLeft: 0,
                    }}
                >
                    {visibleSections.map(({ id, label, blurb }) => {
                        const progress = progressBySection[id];
                        const isActive = active === id;
                        const done = progress.complete && progress.total > 0;
                        const sectionIssues = issuesBySection[id] || [];
                        const step = SECTION_ORDER.findIndex(section => section.id === id) + 1;

                        return (
                            <li key={id} style={{ marginBottom: '2px' }}>
                                <a
                                    href={`#${id}`}
                                    data-section={id}
                                    onClick={event => {
                                        event.preventDefault();
                                        goToSection(id);
                                    }}
                                    aria-current={isActive ? 'true' : undefined}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: '10px',
                                        borderRadius: radius.lg,
                                        padding: '8px 10px',
                                        textDecoration: 'none',
                                        backgroundColor: isActive ? brand[50] : 'transparent',
                                        transition: 'background-color 0.15s',
                                    }}
                                    onMouseEnter={e => {
                                        if (!isActive) e.currentTarget.style.backgroundColor = gray[50];
                                    }}
                                    onMouseLeave={e => {
                                        if (!isActive) e.currentTarget.style.backgroundColor = 'transparent';
                                    }}
                                >
                                    <span
                                        style={{
                                            marginTop: '2px',
                                            display: 'flex',
                                            height: '20px',
                                            width: '20px',
                                            flexShrink: 0,
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            borderRadius: radius.pill,
                                            fontSize: '10px',
                                            fontWeight: 700,
                                            backgroundColor: done
                                                ? brand[600]
                                                : isActive
                                                    ? white
                                                    : gray[100],
                                            color: done
                                                ? white
                                                : isActive
                                                    ? brand[700]
                                                    : ink[500],
                                            boxShadow: !done && isActive
                                                ? `0 0 0 1px ${brand[200]}`
                                                : 'none',
                                        }}
                                        aria-hidden="true"
                                    >
                                        {done ? <Check size={12} strokeWidth={3} /> : step}
                                    </span>

                                    <span style={{ minWidth: 0, flex: 1 }}>
                                        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <span
                                                style={{
                                                    fontSize: '13px',
                                                    fontWeight: 600,
                                                    color: isActive ? brand[700] : ink[800],
                                                }}
                                            >
                                                {label}
                                            </span>
                                            {sectionIssues.length > 0 ? (
                                                <span
                                                    style={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: '2px',
                                                        fontSize: '10px',
                                                        fontWeight: 700,
                                                        color: amber[700],
                                                    }}
                                                    title={`${sectionIssues.length} issue${sectionIssues.length === 1 ? '' : 's'}`}
                                                >
                                                    <AlertTriangle size={12} aria-hidden="true" />
                                                    {sectionIssues.length}
                                                </span>
                                            ) : ''}
                                        </span>
                                        <span
                                            style={{
                                                display: 'block',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                fontSize: '11px',
                                                color: ink[400],
                                            }}
                                        >
                                            {blurb}
                                        </span>
                                    </span>
                                </a>
                            </li>
                        );
                    })}
                </ul>
            </div>
        </nav>
    );
};

export default SectionNav;
